
import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import CleanInterviewSubmissionForm from '@/components/interview/CleanInterviewSubmissionForm';
import DebugInterviewSubmissionForm from '@/components/interview/DebugInterviewSubmissionForm';
import { SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import LanguageToggle from '@/components/ui/language-toggle';

const InterviewSubmissionPage: React.FC = () => {
  console.log('InterviewSubmissionPage: Component is loading...', window.location.href);
  const { requestLink: pathRequestLink } = useParams<{ requestLink: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [decodedRequestLink, setDecodedRequestLink] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, user, signOut, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  
  // Check if debug mode is enabled via URL parameter
  const urlParams = new URLSearchParams(location.search);
  const debugMode = urlParams.get('debug') === 'true';
  
  // Get request link from either path parameter or query parameter
  const queryRequestLink = urlParams.get('request');
  const requestLink = queryRequestLink || pathRequestLink;
  
  useEffect(() => {
    console.log('InterviewSubmissionPage: Effect triggered');
    console.log('InterviewSubmissionPage: requestLink:', requestLink);
    console.log('InterviewSubmissionPage: pathRequestLink:', pathRequestLink);
    console.log('InterviewSubmissionPage: queryRequestLink:', queryRequestLink);
    
    if (!requestLink) {
      console.log('InterviewSubmissionPage: No request link, redirecting');
      toast.error('Ugyldig intervjulenke');
      navigate('/interview-requests');
      return;
    }
    
    if (!SUPABASE_PUBLISHABLE_KEY) {
      toast.error('Configuration error');
      setIsLoading(false);
      return;
    }
    
    try {
      let finalLink = requestLink.trim();
      
      try {
        const decoded = decodeURIComponent(requestLink);
        if (decoded !== requestLink) {
          finalLink = decoded.trim();
        }
      } catch (e) {
        // Use original if decode fails
      }
      
      setDecodedRequestLink(finalLink);
      setIsLoading(false);
      
    } catch (error) {
      setDecodedRequestLink(requestLink);
      setIsLoading(false);
    }
  }, [requestLink, navigate]);
  
  // Show loading state while processing (but don't wait for auth)
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto py-4 px-4">
          <header className="py-4 mb-8 flex items-center justify-between">
            <img 
              src="/lovable-uploads/4fc39b45-cf07-4118-9f14-018db1910dbf.png" 
              alt="Briefr logo" 
              className="h-16"
            />
            <div className="flex items-center gap-2">
              <LanguageToggle />
              {isAuthenticated ? (
                <div className="flex items-center gap-2">
                  {user?.email && (
                    <span className="text-xs text-muted-foreground hidden md:block">
                      {user.email}
                    </span>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={signOut}
                    className="text-xs md:text-sm"
                  >
                    {t('auth.logOut')}
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm"
                  asChild
                  className="text-xs md:text-sm"
                >
                  <Link to="/auth">{t('auth.logIn')}</Link>
                </Button>
              )}
            </div>
          </header>
          <div className="p-8 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
            <p className="mt-4">Laster intervjuforespørsel...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Show error state if no request link
  if (!requestLink && !isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto py-4 px-4">
          <header className="py-4 mb-8 flex items-center justify-between">
            <img 
              src="/lovable-uploads/4fc39b45-cf07-4118-9f14-018db1910dbf.png" 
              alt="Briefr logo" 
              className="h-16"
            />
            <div className="flex items-center gap-2">
              <LanguageToggle />
              {isAuthenticated ? (
                <div className="flex items-center gap-2">
                  {user?.email && (
                    <span className="text-xs text-muted-foreground hidden md:block">
                      {user.email}
                    </span>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={signOut}
                    className="text-xs md:text-sm"
                  >
                    {t('auth.logOut')}
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm"
                  asChild
                  className="text-xs md:text-sm"
                >
                  <Link to="/auth">{t('auth.logIn')}</Link>
                </Button>
              )}
            </div>
          </header>
          <div className="text-center py-8">
            <h1 className="text-2xl font-bold text-red-600">Ugyldig intervjulenke</h1>
            <p className="mt-4">Lenken du prøvde å åpne er ikke gyldig.</p>
          </div>
        </div>
      </div>
    );
  }
  
  const finalRequestLink = decodedRequestLink || requestLink || '';
  console.log('=== FINAL REQUEST LINK COMPUTATION ===');
  console.log('InterviewSubmissionPage: decodedRequestLink:', `"${decodedRequestLink}"`);
  console.log('InterviewSubmissionPage: requestLink:', `"${requestLink}"`);  
  console.log('InterviewSubmissionPage: finalRequestLink:', `"${finalRequestLink}"`);
  console.log('InterviewSubmissionPage: About to render with finalRequestLink:', finalRequestLink);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-4 px-4">
        <header className="py-4 mb-8 flex justify-between items-center">
          <img 
            src="/lovable-uploads/4fc39b45-cf07-4118-9f14-018db1910dbf.png" 
            alt="Briefr logo" 
            className="h-16"
          />
          <div className="flex items-center gap-2">
            {debugMode && (
              <div className="bg-red-500 text-white px-4 py-2 rounded-md shadow-lg font-bold text-sm flex items-center gap-2 animate-pulse z-50 relative">
                🐛 DEBUG MODE ACTIVE
              </div>
            )}
            <LanguageToggle />
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                {user?.email && (
                  <span className="text-xs text-muted-foreground hidden md:block">
                    {user.email}
                  </span>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={signOut}
                  className="text-xs md:text-sm"
                >
                  {t('auth.logOut')}
                </Button>
              </div>
            ) : (
              <Button 
                variant="ghost" 
                size="sm"
                asChild
                className="text-xs md:text-sm"
              >
                <Link to="/auth">{t('auth.logIn')}</Link>
              </Button>
            )}
          </div>
        </header>
        
        
        {debugMode ? (
          <DebugInterviewSubmissionForm 
            requestLink={finalRequestLink} 
            isPageLoading={isLoading}
          />
        ) : (
          <CleanInterviewSubmissionForm 
            requestLink={finalRequestLink} 
          />
        )}
      </div>
    </div>
  );
};

export default InterviewSubmissionPage;
